import _ from 'lodash';
import { createAppSlice } from 'store/utils';
import legacyUserReducer from '_legacy/reducers/userReducer';
import { UserApi } from 'api';
import { RetainedView, UserSettingsFragment } from 'api/fragments';

export interface ZoneMappingEntry {
    key: string;
    value: string;
}

export type ZoneMapping = ZoneMappingEntry[];

export interface ZoneMappingConfig {
    id: string;
    datasetId: number;
    name: string;
    mappings: ZoneMappingEntry[];
    dischMappings?: ZoneMappingEntry[];
}

export interface UserGroup {
    id: string;
    datasetId: number;
    name: string;
    fixtures: string;
    orders: string;
    commonSettings: {
        directionLogic: string;
        quantityFormat: string;
        defaultType: string;
    };
}

export type UserUiSettings = UserSettingsFragment;

export interface LayoutMenuState {
    userSectionExpanded: boolean;
    adminTemplateSectionExpanded: boolean;
    templateSectionExpanded: boolean;
}

export interface UserState {
    id: string;
    userId: number;
    username: string;
    isAdmin: boolean;
    groups: UserGroup[];
    permissions: { groupId: string; canWrite: boolean }[];
    groupId: any;
    group: UserGroup | undefined;
    gridOptions: any;
    gridData: any;
    headingDefaults: any[];
    creationIndex: any;
    dataSize: any;
    reportTitles: any[];
    themeSettings: UserUiSettings;
    uiSettings: UserUiSettings;
    userTimezone: string;
    loading: boolean;
    zoneMapping: ZoneMappingConfig | undefined;
}

const initialState: UserState = {
    id: '',
    userId: -1,
    username: '',
    isAdmin: false,
    groups: [],
    permissions: [],
    groupId: undefined,
    group: undefined,
    gridOptions: {},
    gridData: {},
    headingDefaults: [],
    creationIndex: {},
    dataSize: undefined,
    reportTitles: [],
    themeSettings: {
        newThemeEnabled: false,
        useCondensedView: false,
        useCompactDensityView: false,
        layoutsUserSectionExpanded: true,
        layoutsAdminTemplateSectionExpanded: false,
        layoutsTemplateSectionExpanded: true,
    },
    uiSettings: {
        newThemeEnabled: false,
        useCondensedView: false,
        useCompactDensityView: false,
        layoutsUserSectionExpanded: true,
        layoutsAdminTemplateSectionExpanded: false,
        layoutsTemplateSectionExpanded: true,
    },
    userTimezone: '',
    loading: false,
    zoneMapping: undefined,
};

const userSlice = createAppSlice({
    name: 'user',
    initialState: (): UserState => ({ ...initialState }),
    reducers: (create) => ({
        signedIn: create.reducer<{
            username: string;
            isAdmin: boolean;
            userTimezone: string;
        }>((state, { payload }) => {
            _.assign(
                state,
                _.pick(payload, ['username', 'isAdmin', 'userTimezone'])
            );
        }),
        refreshInfo: create.asyncThunk(() => UserApi.fetchUserInfo(), {
            pending: (state) => {
                state.loading = true;
            },
            rejected: (state) => {
                state.loading = false;
            },
            fulfilled: (state, { payload }) => {
                const update = _.pick(payload, [
                    'id',
                    'userId',
                    'groups',
                    'permissions',
                    'uiSettings',
                    'groupsSettings',
                ]);
                const retainedGroup = getItemByIdOrFirst(
                    update.groups,
                    update.uiSettings.retainedGroup
                );
                _.assign(state, update, {
                    themeSettings: update.uiSettings,
                    group: retainedGroup,
                });
                if (
                    update.uiSettings.zoneMappingConfig &&
                    update.uiSettings.zoneMappingConfig.datasetId ===
                        state.group?.datasetId
                ) {
                    state.zoneMapping = update.uiSettings.zoneMappingConfig;
                } else {
                    state.zoneMapping = undefined;
                }
                state.loading = false;
            },
        }),
        changeZoneMappingConfig: create.asyncThunk(
            (zoneMappingConfigId: string | null) =>
                UserApi.changeZoneMappingConfig(zoneMappingConfigId),
            {
                fulfilled: (state, { payload }) => {
                    state.uiSettings.zoneMappingConfig =
                        payload.zoneMappingConfig ?? undefined;
                    state.zoneMapping = payload.zoneMappingConfig ?? undefined;
                },
            }
        ),
        changeDefaultCargo: create.asyncThunk(
            (cargoId: string | null) => UserApi.changeDefaultCargo(cargoId),
            {
                fulfilled: (state, { payload }) => {
                    state.uiSettings.defaultCargo =
                        payload.defaultCargo ?? undefined;
                },
            }
        ),
        updateLayoutMenuSettings: create.asyncThunk(
            (
                {
                    userSectionExpanded,
                    adminTemplateSectionExpanded,
                    templateSectionExpanded,
                }: LayoutMenuState,
                { getState, rejectWithValue }
            ) => {
                const {
                    user: { uiSettings },
                } = getState() as { user: UserState };

                if (
                    userSectionExpanded ===
                        uiSettings.layoutsUserSectionExpanded &&
                    adminTemplateSectionExpanded ===
                        uiSettings.layoutsAdminTemplateSectionExpanded &&
                    templateSectionExpanded ===
                        uiSettings.layoutsTemplateSectionExpanded
                ) {
                    return rejectWithValue(
                        'Suppressing update, settings are the same'
                    );
                }

                return UserApi.updateUserSettings(
                    userSectionExpanded,
                    adminTemplateSectionExpanded,
                    templateSectionExpanded
                );
            },
            {
                pending: (state) => {
                    state.loading = true;
                },
                rejected: (state) => {
                    state.loading = false;
                },
                fulfilled: (state, { payload }) => {
                    _.assign(state.uiSettings, payload);
                    _.assign(state.themeSettings, payload);
                    state.loading = false;
                },
            }
        ),
        setRetainedView: create.reducer<RetainedView>((state, retainedView) => {
            _.assign(state.uiSettings, { retainedView: retainedView.payload });
        }),
    }),
    extraReducers: (create) =>
        create.addDefaultCase(
            (state, action) => legacyUserReducer(state as any, action) as any
        ),
    selectors: {
        isAdmin: (state) => state.isAdmin,
        userId: (state) => state.userId,
        currentDatasetId: (state): number | undefined => state.group?.datasetId,
        layoutMenu: ({ uiSettings }): LayoutMenuState => ({
            userSectionExpanded: uiSettings.layoutsUserSectionExpanded,
            adminTemplateSectionExpanded:
                uiSettings.layoutsAdminTemplateSectionExpanded,
            templateSectionExpanded: uiSettings.layoutsTemplateSectionExpanded,
        }),
        isNewThemeEnabled: (state) => state.themeSettings.newThemeEnabled,
        userPermissions: (state) => state.permissions,
        userSelectedGroup: (state) => state.group,
        userGroups: (state) => state.groups,
        zoneMapping: (state): ZoneMappingConfig | undefined =>
            state.zoneMapping,
    },
});

export const userSelectors = userSlice.selectors;

export const {
    signedIn: userSignedIn,
    refreshInfo: refreshUserInfo,
    updateLayoutMenuSettings,
    setRetainedView,
    changeZoneMappingConfig,
    changeDefaultCargo,
} = userSlice.actions;

export default userSlice.reducer;

const getItemByIdOrFirst = (items: { id: string }[], id?: string) => {
    return items.find((x) => x.id === id) ?? items[0];
};
